/**
 * ArticleEditPage - Article Edit Page with Tab-based Layout
 *
 * Modern, professional edit page with:
 * - Tab navigation (Tổng quan, Nội dung, Xuất bản, SEO)
 * - Sticky action bar
 * - Collapsible sections
 * - Responsive design
 */

import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { connect } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Dispatch } from 'redux';
import {
	BaseForm,
	CheckBoxList,
	ComboBox,
	DateBox,
	LoadingPanel,
	SwitchBox,
	TextAreaBox,
	TextBox,
} from '../../../components/base';
import { ImageBox } from '../../../components/base/ImageBox';
import TextEditor from '../../../components/base/TextEditor';
import { EditPageLayout, EditPageSection, EditPageField } from '../../../components/container';
import { ComboBoxArticleCategory } from '../../../components/shared/hoc-article-category/ComboBoxArticleCategory';
import { eBaseActionStatus } from '../../../context/base/eBaseActionStatus';
import { ArticleActionConfirmRequest } from '../../../context/models/articles/ArticleActionConfirmRequest';
import {
	ArticleModel,
	dfArticleModel,
	validArticleSchema,
} from '../../../context/models/articles/ArticleModel';
import { eArticleArchivesData } from '../../../context/models/articles/eArticleArchives';
import { eArticleGenresData } from '../../../context/models/articles/eArticleGenres';
import { eArticleStatus, getArticleStatusName } from '../../../context/models/articles/eArticleStatus';
import { ArticleActionTypes } from '../../../context/stores/articles/IArticleActionsTypes';
import { actions } from '../../../context/stores/rootActions';
import { AppState } from '../../../context/stores/rootReducers';
import { slugHelper } from '../../../utils/slugHelper';
import { ArticleButton } from '../button-actions/ArticleButton';
import { ChangeStatusConfirm } from '../change-status-confirm';
import { RelatedArticles } from '../related-articles';
import { history } from '../../../utils/history';
import { FileText, Image as ImageIcon, Send, Search, Info, Calendar } from 'lucide-react';

const mapStateToProps = (state: AppState) => {
	return {
		status: state.article.status,
		detailResponse: state.article.detailResponse,
		selectedIds: state.article.selectedIds,
		showConfirm: state.article.showConfirm,
	};
};

const mapDispatchToProps = (dispatch: Dispatch<ArticleActionTypes>) => ({
	handleGetDetail: (id: string) => {
		dispatch(actions.article.detailRequest(id));
	},
	handleSaveData: (data: ArticleModel) => {
		dispatch(actions.article.saveRequest(data));
	},
	handleChangeStatus: (detail: ArticleActionConfirmRequest) => {
		dispatch(
			actions.article.changeStatusRequest({
				actionStatus: detail.actionStatus,
				articleIds: detail.ids || [],
				receivedBy: detail.receivedBy || '',
				message: detail.message || '',
				subject: detail.subject || '',
			})
		);
	},
	onShowConfirm: (detail: ArticleActionConfirmRequest) => {
		dispatch(actions.article.showConfirm(detail));
	},
});

type IArticleEditPageProps = {
	status: eBaseActionStatus;
	detailResponse?: ArticleModel;
	selectedIds: string[];
	showConfirm: ArticleActionConfirmRequest;
	handleGetDetail: (id: string) => void;
	handleSaveData: (data: ArticleModel) => void;
	handleChangeStatus: (data: ArticleActionConfirmRequest) => void;
	onShowConfirm: (data: ArticleActionConfirmRequest) => void;
};

const ArticleEditPage = (props: IArticleEditPageProps) => {
	const { id } = useParams();
	const { pathname } = useLocation();
	const navigate = useNavigate();
	const articleId = useMemo(() => id || '0', [id]);
	const articleListPath = useMemo(() => {
		if (pathname.startsWith('/articles-published')) return '/articles-published';
		if (pathname.startsWith('/articles-deleted')) return '/articles-deleted';
		if (pathname.startsWith('/articles-editing')) return '/articles-editing';
		if (pathname.startsWith('/articles-removed')) return '/articles-removed';
		if (pathname.startsWith('/articles-sent-approve')) return '/articles-sent-approve';
		if (pathname.startsWith('/articles-sent-edit')) return '/articles-sent-edit';
		if (pathname.startsWith('/articles-wait-approving')) return '/articles-wait-approving';
		if (pathname.startsWith('/articles-wait-editing')) return '/articles-wait-editing';
		if (pathname.startsWith('/articles-approving')) return '/articles-approving';
		return '/articles-template';
	}, [pathname]);

	const { detailResponse, status, showConfirm, selectedIds, handleGetDetail, handleSaveData, onShowConfirm } =
		props;

	const [initialValues, setInitialValues] = useState<ArticleModel>(dfArticleModel);
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
	const isInitializedRef = useRef(false);

	const updateField = useCallback((field: keyof ArticleModel, value: any) => {
		setInitialValues((prev) => ({ ...prev, [field]: value }));
		setHasUnsavedChanges(true);
	}, []);

	const {
		control,
		handleSubmit,
		reset,
		setValue,
		formState: { errors },
	} = useForm<ArticleModel>({
		resolver: yupResolver(validArticleSchema() as any),
		mode: 'all',
		defaultValues: initialValues,
	});

	const onSubmitHandler = (values: ArticleModel) => {
		handleSaveData({ ...values });
		setHasUnsavedChanges(false);
	};

	const watchChangeName = useWatch({ control, name: 'title', defaultValue: '' });

	useEffect(() => {
		if (initialValues.status !== eArticleStatus.PUBLISHED) {
			const slug = slugHelper.toSlug(watchChangeName);
			if (initialValues.titleSlug !== slug) {
				setValue('titleSlug', slug);
				setInitialValues((prev) => ({ ...prev, titleSlug: slug }));
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [watchChangeName, initialValues.status, initialValues.titleSlug]);

	useEffect(() => {
		if (!isInitializedRef.current || Object.keys(initialValues).length === 0) {
			return;
		}
		reset(initialValues);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialValues, reset]);

	useEffect(() => {
		if (detailResponse) {
			const newValues = {
				...detailResponse,
				publishedOnDate: moment(detailResponse.publishedOnDate).format(),
				actionStatus: eArticleStatus.EDITED,
			};
			setInitialValues(newValues);
			reset(newValues);
			setHasUnsavedChanges(false);
			isInitializedRef.current = true;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [detailResponse]);

	useEffect(() => {
		if (articleId !== '0') {
			handleGetDetail(articleId);
		} else {
			setInitialValues(dfArticleModel);
			reset(dfArticleModel);
			isInitializedRef.current = true;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [articleId]);

	useEffect(() => {
		if (status === eBaseActionStatus.reload)
			navigate(history.location?.pathname || '/', { replace: true });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [status]);

	const getStatusBadge = () => {
		switch (initialValues.status) {
			case eArticleStatus.PUBLISHED:
				return { label: 'Đã xuất bản', variant: 'success' as const };
			case eArticleStatus.DRAFT:
				return { label: 'Bản nháp', variant: 'secondary' as const };
			case eArticleStatus.DELETED:
				return { label: 'Đã xóa', variant: 'destructive' as const };
			default:
				return { label: 'Chỉnh sửa', variant: 'warning' as const };
		}
	};

	const statusBadge = getStatusBadge();

	const sectionChecklist = useMemo(() => {
		const overview = [
			Boolean(initialValues.title?.trim()),
			Boolean(initialValues.titleSlug?.trim()),
			Boolean(initialValues.catId),
			Boolean(initialValues.image?.trim()),
		];
		const content = [
			Boolean(initialValues.initContent?.trim()),
			Boolean(initialValues.contentHtml?.trim()),
		];
		const publish = [
			Boolean(initialValues.publishedOnDate),
			Boolean(initialValues.genre),
			Boolean(initialValues.authorPublish?.trim()),
		];
		const seo = [
			Boolean(initialValues.metaTitle?.trim()),
			Boolean(initialValues.metaDescription?.trim()),
		];

		const countDone = (items: boolean[]) => items.filter(Boolean).length;

		return {
			overview: { done: countDone(overview), total: overview.length },
			content: { done: countDone(content), total: content.length },
			publish: { done: countDone(publish), total: publish.length },
			seo: { done: countDone(seo), total: seo.length },
		};
	}, [initialValues]);

	const breadcrumbs = [
		{ label: 'Trang chủ', href: '/' },
		{ label: 'Tin bài', href: articleListPath },
		{ label: articleId !== '0' ? 'Chỉnh sửa bài viết' : 'Tạo mới bài viết' },
	];

	// Handle cancel
	const handleCancel = () => {
		navigate(-1);
	};

	// Tab content: Overview Tab
	const overviewTabContent = (
		<div className='space-y-6'>
			<div className='grid grid-cols-2 gap-3 rounded-xl border border-border/70 bg-muted/20 p-4 lg:grid-cols-4'>
				<div>
					<p className='text-[11px] uppercase tracking-wide text-muted-foreground'>Tổng quan</p>
					<p className='text-base font-semibold'>{sectionChecklist.overview.done}/{sectionChecklist.overview.total}</p>
				</div>
				<div>
					<p className='text-[11px] uppercase tracking-wide text-muted-foreground'>Nội dung</p>
					<p className='text-base font-semibold'>{sectionChecklist.content.done}/{sectionChecklist.content.total}</p>
				</div>
				<div>
					<p className='text-[11px] uppercase tracking-wide text-muted-foreground'>Xuất bản</p>
					<p className='text-base font-semibold'>{sectionChecklist.publish.done}/{sectionChecklist.publish.total}</p>
				</div>
				<div>
					<p className='text-[11px] uppercase tracking-wide text-muted-foreground'>SEO</p>
					<p className='text-base font-semibold'>{sectionChecklist.seo.done}/{sectionChecklist.seo.total}</p>
				</div>
			</div>

			{/* Title & Cover Image */}
			<EditPageSection
				title='Tiêu đề & Hình ảnh'
				icon={<Info className='h-4 w-4' />}
			>
				<div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
					<EditPageField
						label='Tiêu đề bài viết'
						required
						error={errors.title?.message}
						helpText='Tiêu đề hấp dẫn để thu hút người đọc'
					>
						<TextAreaBox
							name='title'
							value={initialValues.title}
							onValueChanged={(value) => {
								updateField('title', value);
							}}
							className='form-control-simple no-resize editor-focus editor-f-22'
							classWrapper='border-bottom'
							sizeClass='sm'
							placeholder='Nhập tiêu đề bài viết...'
						/>
					</EditPageField>

					<div className='space-y-4'>
						<EditPageField label='Slug' error={errors.titleSlug?.message}>
							<TextBox
								name='titleSlug'
								value={initialValues.titleSlug}
								isDisabled={initialValues.status === eArticleStatus.PUBLISHED}
								onValueChanged={(value) => {
									updateField('titleSlug', value);
								}}
								sizeClass='sm'
								className='form-control-simple editor-focus editor-f-16'
								classWrapper='border-bottom'
								helpBlock='Link: /tin-tuc/tieu-de-bai-viet'
							/>
						</EditPageField>

						<EditPageField label='Chuyên mục' error={errors.catId?.message}>
							<ComboBoxArticleCategory
								name='catId'
								value={initialValues.catId}
								onValueChanged={(value) => {
									updateField('catId', value);
								}}
							/>
						</EditPageField>
					</div>
				</div>

				<div className='mt-6'>
					<EditPageField label='Ảnh bìa'>
						<ImageBox
							name='image'
							value={initialValues.image}
							onValueChanged={(value) => {
								updateField('image', value || '');
							}}
						/>
					</EditPageField>
				</div>
			</EditPageSection>

			{/* Sapo (Short Description) */}
			<EditPageSection
				title='Sapo'
				icon={<FileText className='h-4 w-4' />}
			>
				<TextAreaBox
					name='initContent'
					value={initialValues.initContent}
					onValueChanged={(value) => {
						updateField('initContent', value);
					}}
					className='form-control-simple no-resize editor-focus editor-f-18'
					classWrapper='border-bottom'
					sizeClass='sm'
					placeholder='Tóm tắt ngắn gọn nội dung bài viết...'
				/>
			</EditPageSection>
		</div>
	);

	// Tab content: Content Tab
	const contentTabContent = (
		<div className='space-y-6'>
			{/* Main Content */}
			<EditPageSection
				title='Nội dung bài viết'
				icon={<FileText className='h-4 w-4' />}
			>
				<TextEditor
					name='contentHtml'
					value={initialValues.contentHtml}
					onValueChanged={(value) => {
						updateField('contentHtml', value);
					}}
				/>
			</EditPageSection>

			{/* Related Articles */}
			<EditPageSection
				title='Tin liên quan'
				icon={<FileText className='h-4 w-4' />}
				defaultExpanded={false}
			>
				<RelatedArticles />
			</EditPageSection>

			{/* Related Products */}
			<EditPageSection
				title='Sản phẩm liên quan'
				icon={<FileText className='h-4 w-4' />}
				defaultExpanded={false}
			>
				<RelatedArticles />
			</EditPageSection>
		</div>
	);

	// Tab content: Publish Tab
	const publishTabContent = (
		<div className='space-y-6'>
			{/* Publish Settings */}
			<EditPageSection
				title='Cài đặt xuất bản'
				icon={<Send className='h-4 w-4' />}
			>
				<div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
					<EditPageField label='Ngày xuất bản'>
						<DateBox
							name='publishedOnDate'
							value={moment(initialValues.publishedOnDate).format()}
							displayFormat={'dd/MM/yyyy HH:mm'}
							typeDate='datetime'
							onValueChanged={(e) => {
								updateField('publishedOnDate', e.value);
							}}
						/>
					</EditPageField>

					<EditPageField label='Loại tin hiển thị'>
						<ComboBox
							name='genre'
							defaultText='Loại tin'
							value={initialValues.genre}
							dataSource={eArticleGenresData}
							valueExpr='Code'
							displayExpr='Name'
							onValueChanged={(value) => {
								updateField('genre', value);
							}}
							sizeClass='sm'
						/>
					</EditPageField>
				</div>
			</EditPageSection>

			{/* Display Options */}
			<EditPageSection
				title='Tùy chọn hiển thị'
				icon={<Info className='h-4 w-4' />}
			>
				<div className='space-y-4'>
					<div className='flex items-center justify-between p-3 bg-muted/30 rounded-lg'>
						<div>
							<p className='font-medium text-sm'>AMP</p>
							<p className='text-xs text-muted-foreground'>
								Bật tăng tốc trang di động cho Google
							</p>
						</div>
						<SwitchBox
							name='isAMP'
							value={initialValues.isAMP}
							onValueChanged={(value) => {
								updateField('isAMP', value);
							}}
							sizeClass='sm'
						/>
					</div>

					<div className='flex items-center justify-between p-3 bg-muted/30 rounded-lg'>
						<div>
							<p className='font-medium text-sm'>Ẩn nội dung</p>
							<p className='text-xs text-muted-foreground'>
								Chỉ hiển thị tiêu đề và hình ảnh
							</p>
						</div>
						<SwitchBox
							name='isHiddenContent'
							value={initialValues.isHiddenContent}
							onValueChanged={(value) => {
								updateField('isHiddenContent', value);
							}}
							sizeClass='sm'
						/>
					</div>
				</div>
			</EditPageSection>

			{/* Archives */}
			<EditPageSection
				title='Vùng hiển thị'
				icon={<Info className='h-4 w-4' />}
				defaultExpanded={false}
			>
				<CheckBoxList
					name='archives'
					selectedValue={[]}
					dataSource={eArticleArchivesData}
					sizeClass='sm'
					displayExpr='Name'
					keyExpr='Code'
					onSelectionChanged={(_value: number[]) => {
						// Handle archives selection
					}}
				/>
			</EditPageSection>

			{/* Author */}
			<EditPageSection
				title='Tác giả & Nguồn'
				icon={<Info className='h-4 w-4' />}
				defaultExpanded={false}
			>
				<div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
					<EditPageField label='Bút danh'>
						<TextBox
							name='authorPublish'
							value={initialValues.authorPublish}
							onValueChanged={(value) => {
								updateField('authorPublish', value);
							}}
							sizeClass='sm'
							className='form-control-simple editor-focus editor-f-18'
							classWrapper='border-bottom'
						/>
					</EditPageField>

					<EditPageField label='Nguồn'>
						<TextBox
							name='sourceLink'
							value={initialValues.sourceLink}
							onValueChanged={(value) => {
								updateField('sourceLink', value);
							}}
							sizeClass='sm'
							className='form-control-simple editor-focus editor-f-18'
							classWrapper='border-bottom'
						/>
					</EditPageField>
				</div>
			</EditPageSection>
		</div>
	);

	// Tab content: SEO Tab
	const seoTabContent = (
		<div className='space-y-6'>
			<EditPageSection
				title='Công cụ tìm kiếm'
				icon={<Search className='h-4 w-4' />}
				defaultExpanded={false}
			>
				<div className='space-y-6'>
					{/* SEO Preview */}
					<div className='p-4 bg-muted/30 rounded-lg'>
						<p className='text-sm font-medium mb-3'>Xem trước kết quả tìm kiếm</p>
						<div className='bg-background p-4 rounded-lg border'>
							<p className='text-blue-600 text-lg hover:underline'>
								{initialValues.metaTitle || initialValues.title || 'Tiêu đề bài viết'}
							</p>
							<p className='text-green-700 text-sm'>
								https://example.com/tin-tuc/{initialValues.titleSlug || 'tieu-de-bai-viet'}
							</p>
							<p className='text-muted-foreground text-sm mt-1'>
								{initialValues.metaDescription || initialValues.initContent || 'Mô tả bài viết sẽ hiển thị ở đây...'}
							</p>
						</div>
					</div>

					<EditPageField
						label='Meta Title'
						helpText='Tiêu đề hiển thị trên Google (tối đa 60 ký tự)'
					>
						<TextBox
							name='metaTitle'
							value={initialValues.metaTitle}
							onValueChanged={(value) => {
								updateField('metaTitle', value);
							}}
							sizeClass='sm'
							className='form-control-simple editor-focus editor-f-18'
							classWrapper='border-bottom'
						/>
					</EditPageField>

					<EditPageField
						label='Meta Description'
						helpText='Mô tả ngắn cho SEO (tối đa 160 ký tự)'
					>
						<TextAreaBox
							name='metaDescription'
							value={initialValues.metaDescription}
							onValueChanged={(value) => {
								updateField('metaDescription', value);
							}}
							sizeClass='sm'
							className='form-control-simple editor-focus editor-f-18'
							classWrapper='border-bottom'
						/>
					</EditPageField>
				</div>
			</EditPageSection>
		</div>
	);

	// Tabs configuration
	const tabs = [
		{ id: 'overview', label: 'Tổng quan', icon: <Info className='h-4 w-4' />, badge: `${sectionChecklist.overview.done}/${sectionChecklist.overview.total}`, content: overviewTabContent },
		{ id: 'content', label: 'Nội dung', icon: <FileText className='h-4 w-4' />, badge: `${sectionChecklist.content.done}/${sectionChecklist.content.total}`, content: contentTabContent },
		{ id: 'publish', label: 'Xuất bản', icon: <Send className='h-4 w-4' />, badge: `${sectionChecklist.publish.done}/${sectionChecklist.publish.total}`, content: publishTabContent },
		{ id: 'seo', label: 'SEO', icon: <Search className='h-4 w-4' />, badge: `${sectionChecklist.seo.done}/${sectionChecklist.seo.total}`, content: seoTabContent },
	];

	// Action buttons
	const actionButtons = (
		<ArticleButton
			isOwner={true}
			articleStatus={initialValues.status}
			isActionDisable={true}
			onActionConfirm={(actionChange) => {
				onShowConfirm({ isShow: true, actionStatus: actionChange, ids: [] });
				setInitialValues((prev) => ({ ...prev, actionStatus: actionChange }));
			}}
			handleSubmit={handleSubmit(onSubmitHandler as any)}
			isSaving={status === eBaseActionStatus.saving}
		/>
	);

	if (status === eBaseActionStatus.loading && !detailResponse) {
		return (
			<div className='min-h-screen flex items-center justify-center'>
				<LoadingPanel />
			</div>
		);
	}

	return (
		<>
			<EditPageLayout
				title='Viết nội dung Tin bài'
				description={`${articleId !== '0' ? 'Chỉnh sửa' : 'Tạo mới'} bài viết`}
				tabs={tabs}
				actions={actionButtons}
				isSaving={status === eBaseActionStatus.saving}
				onSave={handleSubmit(onSubmitHandler as any)}
				onCancel={handleCancel}
				breadcrumbs={breadcrumbs}
				status={statusBadge.label}
				statusVariant={statusBadge.variant}
				lastModified={detailResponse?.lockedOnDate || detailResponse?.publishedOnDate}
				hasUnsavedChanges={hasUnsavedChanges}
			/>

			{showConfirm && showConfirm.isShow && (
				<ChangeStatusConfirm
					isShow={showConfirm.isShow}
					onClose={() => onShowConfirm({ isShow: false, actionStatus: showConfirm.actionStatus, ids: [] })}
					onConfirmStatus={handleSubmit(onSubmitHandler as any)}
					isLoading={status === eBaseActionStatus.loading}
					mesageText={`Bạn chắc chắn muốn ${getArticleStatusName(showConfirm.actionStatus)} bài viết này?`}
					actionStatus={showConfirm.actionStatus}
					selectedIds={selectedIds}
				/>
			)}
		</>
	);
};

export default connect(mapStateToProps, mapDispatchToProps)(ArticleEditPage);
